import moment from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileInvoiceDollar,
  faShieldAlt,
  faClipboardList,
} from '@fortawesome/free-solid-svg-icons';
import { getQuotationTypeName } from '../../constants';
import { isNewBrokerageSystem, getBrokeragePerPayment } from '../../helpers/brokerageHelpers';
import {
  CompletedCard,
  CompletedCardHeader,
  CompletedCardIcon,
  CompletedCardTitle,
  CompletedFieldGrid,
  CompletedField,
} from '../../../screens/CompletedQuotes/CompletedV2.styles';

function CompletedQuoteScreenInfoAuto({ rc_quotation, risk_quotations }) {
  const { quoteTypes } = useSelector((store) => store.utilities.quoteTypes);

  const get_amount_to_pay = () => {
    let primaGuaranteesPrices = 0;

    if (rc_quotation.company_id === 2) {
      rc_quotation.guarantees.forEach((guarantee) => {
        primaGuaranteesPrices += parseFloat(guarantee.amount) || 0;
      });
    }

    const amount_to_pay = parseFloat(
      rc_quotation.amount / rc_quotation.payment_frequency
      + getBrokeragePerPayment(rc_quotation)
      + (rc_quotation.quotation_services
        && rc_quotation.quotation_services.length > 0
        ? rc_quotation.quotation_services[0].amount_gross
        : 0),
    );

    return (
      amount_to_pay
      + primaGuaranteesPrices / rc_quotation.payment_frequency
    ).toFixed(2);
  };

  const getCompanyName = () => {
    if (rc_quotation.company) return rc_quotation.company.name;
    if (rc_quotation.preventivas_company) return rc_quotation.preventivas_company.company_name;
    return '';
  };

  const getPaidAmount = () => {
    if (rc_quotation.amount_paid === null) {
      return get_amount_to_pay().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (rc_quotation.amount_paid !== rc_quotation.amount) {
      return get_amount_to_pay().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return (get_amount_to_pay() * rc_quotation.payment_frequency).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const renderStandardQuotationCard = () => (
    <CompletedCard className="mb-3 polizze-emessa-status-step-three">
      <CompletedCardHeader>
        <CompletedCardIcon>
          <FontAwesomeIcon icon={faFileInvoiceDollar} />
        </CompletedCardIcon>
        <CompletedCardTitle>
          Dati
          {' '}
          {getQuotationTypeName(rc_quotation.quote_type, quoteTypes)}
        </CompletedCardTitle>
      </CompletedCardHeader>

      <CompletedFieldGrid>
        <CompletedField>
          <dt>Compagnia</dt>
          <dd>{getCompanyName()}</dd>
        </CompletedField>

        <CompletedField>
          <dt>Stato</dt>
          <dd>
            {rc_quotation.status === 7 && 'Polizza Emmesa'}
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Data Decorrenza</dt>
          <dd>
            {moment(rc_quotation.insurance_request.policy_effective_date)
              .toDate()
              .toLocaleDateString('it-IT')}
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Freq Pagamento</dt>
          <dd>
            {rc_quotation.payment_frequency === 1 ? 'Annuo' : 'Semestrale'}
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Pagato</dt>
          <dd>
            {rc_quotation.amount_paid === null
              ? '0'
              : rc_quotation.amount_paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {' '}
            €
          </dd>
        </CompletedField>

        {rc_quotation.payment_frequency === 2 && (
          <CompletedField>
            <dt>Da Pagare</dt>
            <dd>
              {rc_quotation.amount_paid === rc_quotation.amount
                ? '0'
                : (
                  Math.ceil(rc_quotation.amount * 100) / 100
                  - rc_quotation.amount_paid
                ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {' '}
              €
            </dd>
          </CompletedField>
        )}

        <CompletedField>
          <dt>Pross Pagamento</dt>
          <dd>
            {moment(rc_quotation.insurance_request.policy_effective_date)
              .add(Math.floor(365 / rc_quotation.payment_frequency), 'days')
              .toDate()
              .toLocaleDateString('it-IT')}
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Premio</dt>
          <dd>
            {(
              rc_quotation.amount
              - parseFloat(rc_quotation.issuance_fee)
              - (rc_quotation.is_facile_quote === 1 ? 20.9 : 0)
            ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {' '}
            €
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Spese di Emissione</dt>
          <dd>
            {(
              parseFloat(rc_quotation.issuance_fee)
              + (rc_quotation.is_facile_quote === 1 ? 20.9 : 0)
            ).toFixed(2)}
            {' '}
            €
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Provvigioni</dt>
          <dd>
            {rc_quotation.commision.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {' '}
            €
          </dd>
        </CompletedField>
        {rc_quotation.insurance_request.insurance_request_extra_services
          && rc_quotation.insurance_request.insurance_request_extra_services.length > 0 && (
            <>
              <CompletedField>
                <dt>
                  {rc_quotation.insurance_request.insurance_request_extra_services[0].service_name}
                </dt>
                <dd>
                  {(parseFloat(
                    rc_quotation.insurance_request.insurance_request_extra_services[0].total_price,
                  )).toFixed(2)}
                  {' '}
                  €
                </dd>
              </CompletedField>
            </>
        )}

        {rc_quotation.quote_type === 'res_civile' && (
          <CompletedField>
            <dt>Brokeraggio</dt>
            <dd>
              {rc_quotation.brokerage_amount.toFixed(2)}
              {' '}
              €
            </dd>
          </CompletedField>
        )}

        {rc_quotation.quotation_services.length > 0 && (
          <CompletedField>
            <dt>Servizio</dt>
            <dd>
              {rc_quotation.quotation_services[0].amount_gross.toFixed(2)}
              {' '}
              €
            </dd>
          </CompletedField>
        )}
        {rc_quotation.quote_type === 'res_civile' && rc_quotation.payment_frequency == 2 && isNewBrokerageSystem(rc_quotation) && (
          <CompletedField>
            <dt>Brokeraggio Semestrale</dt>
            <dd>
              {(
                rc_quotation.brokerage_amount
                / rc_quotation.payment_frequency
              ).toFixed(2)}
              {' '}
              &euro;
            </dd>
          </CompletedField>
        )}
        {/* Totale (temporarily hidden)
        <CompletedField className="full total">
          <dt>Totale</dt>
          <dd>
            {(
              rc_quotation.amount
              + rc_quotation.brokerage_amount
              + (rc_quotation.insurance_request.insurance_request_extra_services && rc_quotation.insurance_request.insurance_request_extra_services.length > 0
                && parseFloat(
                  rc_quotation.insurance_request.insurance_request_extra_services[0].total_price,
                )
              )
              + (rc_quotation.quotation_services.length > 0
                ? rc_quotation.quotation_services[0].amount_gross
                : 0)
            ).toFixed(2)}
            {' '}
            €
          </dd>
        </CompletedField>
        */}
      </CompletedFieldGrid>
    </CompletedCard>
  );

  const renderPrimaQuotationCard = () => (
    <CompletedCard className="mb-3 polizze-emessa-status-step-three">
      <CompletedCardHeader>
        <CompletedCardIcon>
          <FontAwesomeIcon icon={faFileInvoiceDollar} />
        </CompletedCardIcon>
        <CompletedCardTitle>
          Dati
          {' '}
          {getQuotationTypeName(rc_quotation.quote_type, quoteTypes)}
        </CompletedCardTitle>
      </CompletedCardHeader>

      <CompletedFieldGrid>
        <CompletedField>
          <dt>Compagnia</dt>
          <dd>Ipitq ma</dd>
        </CompletedField>

        <CompletedField>
          <dt>Stato</dt>
          <dd>
            {rc_quotation.status === 7 && 'Polizza Emmesa'}
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Data Decorrenza</dt>
          <dd>
            {moment(rc_quotation.insurance_request.policy_effective_date)
              .toDate()
              .toLocaleDateString('it-IT')}
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Polizza Pagata</dt>
          <dd>SI</dd>
        </CompletedField>

        <CompletedField>
          <dt>Freq Pagamento</dt>
          <dd>
            {rc_quotation.payment_frequency === 1 ? 'Annuo' : 'Semestrale'}
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Pagato</dt>
          <dd>
            {getPaidAmount()}
            {' '}
            €
          </dd>
        </CompletedField>

        {rc_quotation.payment_frequency === 2 && (
          <CompletedField>
            <dt>Da Pagare</dt>
            <dd>
              {rc_quotation.amount_paid === rc_quotation.amount
                ? '0'
                : get_amount_to_pay().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {' '}
              €
            </dd>
          </CompletedField>
        )}

        <CompletedField>
          <dt>Pross Pagamento</dt>
          <dd>
            {moment(rc_quotation.insurance_request.policy_effective_date)
              .add(Math.floor(365 / rc_quotation.payment_frequency), 'days')
              .toDate()
              .toLocaleDateString('it-IT')}
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Provvigioni</dt>
          <dd>
            {rc_quotation.commision.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {' '}
            €
          </dd>
        </CompletedField>

        {rc_quotation.quote_type === 'res_civile' && (
          <CompletedField>
            <dt>Brokeraggio</dt>
            <dd>
              {rc_quotation.brokerage_amount.toFixed(2)}
              {' '}
              €
            </dd>
          </CompletedField>
        )}

        {rc_quotation.quotation_services.length > 0 && (
          <CompletedField>
            <dt>Servizio</dt>
            <dd>
              {rc_quotation.quotation_services[0].amount_gross.toFixed(2)}
              {' '}
              €
            </dd>
          </CompletedField>
        )}
        {rc_quotation.quote_type === 'res_civile' && rc_quotation.payment_frequency == 2 && isNewBrokerageSystem(rc_quotation) && (
          <CompletedField>
            <dt>Brokeraggio Semestrale</dt>
            <dd>
              {(
                rc_quotation.brokerage_amount
                / rc_quotation.payment_frequency
              ).toFixed(2)}
              {' '}
              &euro;
            </dd>
          </CompletedField>
        )}
        {/* Totale (temporarily hidden)
        <CompletedField className="full total">
          <dt>Totale</dt>
          <dd>
            {get_amount_to_pay() * rc_quotation.payment_frequency}
            {' '}
            €
          </dd>
        </CompletedField>
        */}
      </CompletedFieldGrid>
    </CompletedCard>
  );

  const renderGuaranteesCard = () => (
    <CompletedCard className="mb-3 polizze-emessa-status-step-three">
      <CompletedCardHeader>
        <CompletedCardIcon>
          <FontAwesomeIcon icon={faShieldAlt} />
        </CompletedCardIcon>
        <CompletedCardTitle>Dati Garanzie</CompletedCardTitle>
      </CompletedCardHeader>

      <CompletedFieldGrid>
        {rc_quotation.guarantees.map((guarantee, index) => {
          const guaranteeData = rc_quotation.quotation_data_prima?.quotation_data_prima_guarantees?.find(
            (g) => g.slug === guarantee.guarantees_slug,
          );
          return (
            <CompletedField key={index}>
              <dt>
                {guaranteeData?.name || 'Garanzia'}
              </dt>
              <dd>
                {guarantee.amount}
                {' '}
                €
              </dd>
            </CompletedField>
          );
        })}
      </CompletedFieldGrid>
    </CompletedCard>
  );

  const renderRiskQuotationCard = (risk_quotation) => (
    <CompletedCard className="mb-3 polizze-emessa-status-step-three">
      <CompletedCardHeader>
        <CompletedCardIcon>
          <FontAwesomeIcon icon={faClipboardList} />
        </CompletedCardIcon>
        <CompletedCardTitle>
          Dati
          {' '}
          {getQuotationTypeName(risk_quotation.quote_type, quoteTypes)}
        </CompletedCardTitle>
      </CompletedCardHeader>

      <CompletedFieldGrid>
        <CompletedField>
          <dt>Compagnia</dt>
          <dd>{risk_quotation.company.name}</dd>
        </CompletedField>

        <CompletedField>
          <dt>Stato</dt>
          <dd>
            {risk_quotation.status === 7 && 'Polizza Emmesa'}
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Data Decorrenza</dt>
          <dd>
            {moment(risk_quotation.insurance_request.policy_effective_date)
              .toDate()
              .toLocaleDateString('it-IT')}
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Freq Pagamento</dt>
          <dd>
            {risk_quotation.payment_frequency === 1 ? 'Annuo' : 'Semestrale'}
          </dd>
        </CompletedField>

        <CompletedField>
          <dt>Pagato</dt>
          <dd>
            {risk_quotation.amount_paid === null
              ? '0'
              : risk_quotation.amount_paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {' '}
            €
          </dd>
        </CompletedField>

        {risk_quotation.payment_frequency === 2 && (
          <CompletedField>
            <dt>Da Pagare</dt>
            <dd>
              {risk_quotation.amount_paid === risk_quotation.amount
                ? '0.00'
                : (
                  Math.ceil(risk_quotation.amount * 100) / 100
                  - risk_quotation.amount_paid
                ).toFixed(2)}
              {' '}
              €
            </dd>
          </CompletedField>
        )}

        <CompletedField>
          <dt>Premio</dt>
          <dd>
            {risk_quotation.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {' '}
            €
          </dd>
        </CompletedField>

        {/* Totale (temporarily hidden)
        <CompletedField className="full total">
          <dt>Totale</dt>
          <dd>
            {risk_quotation.amount.toFixed(2)}
            {' '}
            €
          </dd>
        </CompletedField>
        */}
      </CompletedFieldGrid>
    </CompletedCard>
  );

  return (
    <>
      {/* Main RC Quotation Card */}
      <div className="col-12 col-md-6 mb-3">
        {rc_quotation.company_id !== 2
          ? renderStandardQuotationCard()
          : renderPrimaQuotationCard()}
      </div>

      {/* Guarantees Card - only for Prima (company_id === 2) */}
      {rc_quotation.guarantees && rc_quotation.guarantees.length !== 0 && (
        <div className="col-12 col-md-6 mb-3">
          {renderGuaranteesCard()}
        </div>
      )}

      {/* Risk Quotations Cards */}
      {risk_quotations.map((risk_quotation, index) => (
        <div className="col-12 col-md-6 mb-3" key={`risk-${index}`}>
          {renderRiskQuotationCard(risk_quotation, index)}
        </div>
      ))}
    </>
  );
}

export default CompletedQuoteScreenInfoAuto;
